import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
  Snackbar,
  CircularProgress
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import QrCodeIcon from "@mui/icons-material/QrCode";
import blobStorageService from "../../utils/blobStorage";

const EncryptedOutput = ({
  encryptedData,
  onDownload,
  onUploadToBlobStorage,
  ipfsHash,
  isUploading
}) => {
  const [expanded, setExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(encryptedData);
    setCopySuccess(true);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Encrypted Data
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          onClick={handleExpandClick}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ mb: 2 }}
        >
          {expanded ? "Hide" : "Show"} Encrypted Data
        </Button>

        <Collapse in={expanded}>
          <TextField
            fullWidth
            multiline
            rows={6}
            value={encryptedData}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
            }}
            sx={{ mb: 2 }}
          />
        </Collapse>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyToClipboard}
          >
            Copy to Clipboard
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
          >
            Download Encrypted Data
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            onClick={onUploadToBlobStorage}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Upload to Blob Storage"}
          </Button>
        </Box>
      </Box>

      {ipfsHash && (
        <Box sx={{ mt: 3 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Storage Link
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField
                fullWidth
                value={ipfsHash}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <Tooltip title="Copy Storage URL">
                      <IconButton
                        edge="end"
                        onClick={() => {
                          navigator.clipboard.writeText(ipfsHash);
                          setCopySuccess(true);
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                sx={{ mr: 1 }}
              />

              <Tooltip title="Show QR Code">
                <IconButton onClick={() => setShowQRCode(!showQRCode)}>
                  <QrCodeIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                href={ipfsHash}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Storage Link
              </Button>
            </Box>

            {showQRCode && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={blobStorageService.generateQRCode(ipfsHash)}
                  alt="QR Code for Storage Link"
                  style={{ maxWidth: '200px', height: 'auto' }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success">
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EncryptedOutput;